import React, { useState, useEffect } from 'react';
import { Carousel, CarouselFormData } from '../types/carousel';
import { toast } from "sonner";

interface CarouselFormProps {
  onSubmit: (data: CarouselFormData) => void;
  onCancel: () => void;
  loading: boolean;
  initialData?: Carousel | null;
}

const CarouselForm: React.FC<CarouselFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  initialData,
}) => {
  const [formData, setFormData] = useState<CarouselFormData>({
    title: '',
    description: '',
    cta_text: '',
    preset_id_template: '',
    media_type: 'image',
    media: null,
    is_visible: true,
  });

  const [mediaPreview, setMediaPreview] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        cta_text: initialData.cta_text || '',
        preset_id_template: initialData.preset_id_template || '',
        media_type: initialData.media_type || 'image',
        media: null,
        is_visible: initialData.is_visible ?? true,
      });
      if (initialData.media_url) {
        setMediaPreview(initialData.media_url);
      }
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      const validVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
      const validTypes = formData.media_type === 'image' ? validImageTypes : validVideoTypes;

      if (!validTypes.includes(file.type)) {
        if (formData.media_type === 'image') {
          toast.error('Please upload a valid image file (JPEG, PNG, WebP, or GIF)');
        } else {
          toast.error('Please upload a valid video file (MP4, WebM, MOV, AVI, or MPEG)');
        }
        e.target.value = '';
        return;
      }

      const maxSize = formData.media_type === 'image' ? 10 * 1024 * 1024 : 100 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File size must be less than ${formData.media_type === 'image' ? '10MB' : '100MB'}`);
        e.target.value = '';
        return;
      }

      setFormData((prev) => ({ ...prev, media: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleRemoveMedia = () => {
    setFormData((prev) => ({ ...prev, media: null }));
    setMediaPreview(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!initialData && !formData.media) {
      toast.error('Please upload a media file');
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-semibold mb-1">
        {initialData ? 'Edit Carousel' : 'Create Carousel'}
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {initialData ? 'Update the carousel details below.' : 'Fill in the carousel details below.'}
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
            placeholder="Enter carousel title"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
            placeholder="Enter description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CTA Text
          </label>
          <input
            type="text"
            name="cta_text"
            value={formData.cta_text}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
            placeholder="Enter CTA text"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Preset ID Template *
          </label>
          <input
            type="text"
            name="preset_id_template"
            value={formData.preset_id_template}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
            placeholder="Enter preset ID template"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Media Type *
          </label>
          <select
            name="media_type"
            value={formData.media_type}
            onChange={(e) => {
              handleChange(e);
              // Clear media when switching type
              setFormData((prev) => ({ ...prev, media: null, media_type: e.target.value as 'image' | 'video' }));
              setMediaPreview(null);
            }}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent bg-white"
          >
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Upload Media (Image/Video) {!initialData && '*'}
          </label>
          <input
            type="file"
            accept={formData.media_type === 'image'
              ? "image/jpeg,image/png,image/webp,image/gif"
              : "video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
            }
            onChange={handleMediaChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
          />
          {initialData && !formData.media && mediaPreview && (
            <p className="mt-1 text-sm text-green-600">
              Current media uploaded
            </p>
          )}
          {mediaPreview && (
            <div className="mt-4 relative">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Media Preview</p>
                <button
                  type="button"
                  onClick={handleRemoveMedia}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Remove Media
                </button>
              </div>
              {formData.media_type === 'image' ? (
                <img
                  src={mediaPreview}
                  alt="Carousel preview"
                  className="w-full max-full rounded-lg border-2 border-gray-300 object-cover"
                  style={{ maxHeight: '256px' }}
                />
              ) : (
                <video
                  src={mediaPreview}
                  controls
                  className="w-full max-w-md rounded-lg border-2 border-gray-300"
                  style={{ maxHeight: '300px' }}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center pt-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="is_visible"
              checked={formData.is_visible}
              onChange={handleChange}
              className="w-5 h-5 text-berrys-orange border-gray-300 rounded focus:ring-berrys-orange"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              Visible in Carousel
            </span>
          </label>
        </div>

        <div className="flex space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-berrys-orange hover:bg-berrys-orange-dark text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {loading
              ? initialData
                ? 'Updating...'
                : 'Creating...'
              : initialData
              ? 'Update'
              : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CarouselForm;
